export const TermsSection = () => {
  return (
    <section id="terms" className="scroll-mt-24 mb-32">
      <h3 className="text-2xl font-semibold text-white mb-6">Terms of Service</h3>
      <div className="prose max-w-none space-y-6 text-gray-300">
        <p className="text-gray-300">
          Last updated: January 2025
        </p>
        <p>
          Welcome to Property Vue. These Terms of Service ("Terms") govern your access to and use of Property Vue's services, including our website and application (collectively, the "Service"). By accessing or using Property Vue, you agree to be bound by these Terms.
        </p>
        
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-white">1. Acceptance of Terms</h4>
            <p>
              By creating an account, accessing, or using Property Vue, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use the Service.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">2. Description of Service</h4>
            <p>
              Property Vue is a construction documentation platform that provides:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Unlimited photos with automatic timestamping</li>
              <li>Unlimited members</li>
              <li>Unlimited projects with organization by category</li>
              <li>Search and retrieval of stored images</li>
            </ul>
            <p className="mt-2">
              The Service is designed to help construction professionals store, organize, and manage job site photos for documentation, dispute resolution, and contract compliance.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">3. Account Registration and Responsibilities</h4>
            <p>You must create an account using a valid email address and password to use the Service. You are responsible for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Maintaining the accuracy and completeness of information provided in your account</li>
              <li>Maintaining the security and confidentiality of your login credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying Property Vue immediately of any unauthorized use of your account</li>
              <li>Maintaining appropriate use of your account</li>
            </ul>
            <p className="mt-2">
              You agree not to use Property Vue for any illegal purpose, to upload content that violates the rights of others, or to engage in any activity that disrupts or interferes with the Service.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">4. Subscription Plans and Billing</h4>
            <p>
              Property Vue offers a full-access subscription at $20 per month. By subscribing, you agree to pay the applicable fees as displayed at the time of purchase. Subscriptions are billed monthly and renew automatically unless canceled.
            </p>
            <p className="mt-3">
              <strong className="text-white">Payment Processing:</strong> All payments are processed through Stripe. By providing payment information, you authorize Property Vue to charge your payment method for the subscription fee.
            </p>
            <p className="mt-3">
              <strong className="text-white">Payment Failure:</strong> If payment fails, your subscription will be automatically cancelled. You will be notified of payment failures and given an opportunity to update your payment method.
            </p>
            <p className="mt-3">
              <strong className="text-white">Cancellation:</strong> You may cancel your subscription at any time by contacting us. Cancellation must be requested manually. Upon cancellation, your subscription will remain active until the end of your current billing period.
            </p>
            <p className="mt-3">
              <strong className="text-white">Refunds:</strong> Refunds are available upon request, subject to our refund policy. Contact us at{" "}
              <a href="mailto:help@propertyvue.io" className="text-primary/80 hover:text-primary hover:underline">
                help@propertyvue.io
              </a> to request a refund.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">5. User Content and Ownership</h4>
            <p>
              You retain full ownership of all images, data, and content you upload to Property Vue ("User Content"). By uploading User Content, you grant Property Vue a non-exclusive, worldwide, royalty-free license to store, process, and display your content solely for the purpose of providing the Service to you.
            </p>
            <p className="mt-2">
              You represent and warrant that you have all necessary rights to upload User Content and that your User Content does not violate any third-party rights or applicable laws.
            </p>
            <p className="mt-3">
              <strong className="text-white">Data Export:</strong> You may download and export your User Content at any time through the Service.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">6. Service Availability and Modifications</h4>
            <p>
              Property Vue reserves the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice. We will make reasonable efforts to notify users of significant changes that may affect their use of the Service.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">7. Limitation of Liability</h4>
            <p>Property Vue is provided "as is" and "as available." To the maximum extent permitted by law, Property Vue, LLC shall not be liable for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of data, profits, or business opportunities</li>
              <li>Service interruptions, errors, or technical issues</li>
              <li>Decisions made based on information provided through the Service</li>
              <li>Data loss resulting from user error, including accidental deletion</li>
            </ul>
            <p className="mt-2">
              Property Vue, LLC's total liability for any claims arising from or related to the Service shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">8. Legal Compliance and Law Enforcement</h4>
            <p>
              Property Vue may be required to disclose User Content or account information in response to valid legal requests from law enforcement or governmental authorities. We will comply with such requests as required by law and will notify users when legally permitted to do so.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">9. Termination and Data Retention</h4>
            <p>
              Property Vue reserves the right to suspend or terminate your account at any time for violation of these Terms, illegal activity, or any other reason we deem necessary to protect the Service or other users. Upon termination, your right to use the Service will immediately cease.
            </p>
            <p className="mt-3">
              <strong className="text-white">Account Cancellation:</strong> You may cancel your account at any time by contacting us. Upon cancellation, your data will be retained for 30 days, after which it will be permanently deleted. You may request data export before cancellation.
            </p>
            <p className="mt-3">
              <strong className="text-white">Deleted Content:</strong> Images and other content that you delete will be retained for up to 30 days before permanent deletion, allowing for recovery if needed.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">10. Changes to Terms</h4>
            <p>
              Property Vue reserves the right to modify these Terms at any time. We will notify users of material changes via email at least 30 days before they take effect. Your continued use of the Service after such changes constitutes acceptance of the modified Terms.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">11. Governing Law</h4>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved in the courts of Florida.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-white">12. Contact Information</h4>
            <p>
              If you have questions about these Terms, please contact Property Vue, LLC at{" "}
              <a href="mailto:help@propertyvue.io" className="text-primary/80 hover:text-primary hover:underline">
                help@propertyvue.io
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
